import { GridsterItemConfig } from '@unistocks/components/gridster';
import { DashboardItemDto, DashboardItemType } from '@unistocks/proxy/axios-client';

type DashboardLayoutItemDefaults = Omit<GridsterItemConfig, 'id'>;

/** Default gridster layout for each dashboard item type. Mirrors the API defaults */
export const DEFAULT_GRIDSTER_ITEMS: Record<DashboardItemType, DashboardLayoutItemDefaults> = {
  [DashboardItemType.Date]: { x: 0, y: 0, cols: 6, rows: 3, minItemCols: 6, minItemRows: 3 },
  [DashboardItemType.PortfolioOverview]: {
    x: 18,
    y: 0,
    cols: 6,
    rows: 8,
    minItemCols: 3,
    minItemRows: 4
  },
  [DashboardItemType.PortfolioHistory]: {
    x: 6,
    y: 0,
    cols: 12,
    rows: 8,
    minItemCols: 6,
    minItemRows: 4
  },
  [DashboardItemType.SavingsGoal]: {
    x: -1,
    y: -1,
    cols: 12,
    rows: 8,
    minItemCols: 4,
    minItemRows: 2
  },
  [DashboardItemType.AssetList]: { x: 0, y: 9, cols: 6, rows: 7, minItemCols: 3, minItemRows: 3 },
  [DashboardItemType.EventView]: { x: 0, y: 3, cols: 6, rows: 6, minItemCols: 3, minItemRows: 1 },
  [DashboardItemType.NetSavings]: {
    x: -1,
    y: -1,
    cols: 10,
    rows: 7,
    minItemCols: 6,
    minItemRows: 4
  }
};

const parseSavedLayout = (savedLayoutJson: string | null): Map<string, GridsterItemConfig> => {
  const layouts = new Map<string, GridsterItemConfig>();

  if (!savedLayoutJson) {
    return layouts;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(savedLayoutJson);
  } catch {
    return layouts;
  }

  if (!Array.isArray(parsed)) {
    return layouts;
  }

  for (const entry of parsed as Partial<GridsterItemConfig>[]) {
    if (!entry || typeof entry.id !== 'string') {
      continue;
    }

    const { id, x, y, cols, rows } = entry;
    if (
      typeof x !== 'number' ||
      typeof y !== 'number' ||
      typeof cols !== 'number' ||
      typeof rows !== 'number' ||
      !Number.isFinite(x) ||
      !Number.isFinite(y) ||
      !Number.isFinite(cols) ||
      !Number.isFinite(rows)
    ) {
      continue;
    }

    layouts.set(id, {
      id,
      x,
      y,
      cols,
      rows
    });
  }

  return layouts;
};

/** Merge the saved Dashboard.Layout setting with defaults for new items */
export const computeLayout = (
  savedLayoutJson: string | null,
  items: DashboardItemDto[]
): GridsterItemConfig[] => {
  const savedLayout = parseSavedLayout(savedLayoutJson);

  return items.map(item => {
    const defaults = DEFAULT_GRIDSTER_ITEMS[item.type];
    const saved = savedLayout.get(item.id);

    if (!saved) {
      return { ...defaults, id: item.id };
    }

    return {
      ...defaults,
      ...saved,
      id: item.id,
      minItemCols: defaults.minItemCols,
      minItemRows: defaults.minItemRows
    };
  });
};
